import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useNavigation } from "@refinedev/core";
import {
  Alert,
  Button,
  Card,
  Flex,
  Input,
  Modal,
  Space,
  Spin,
  Switch,
  Typography,
  message,
  theme,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  DeleteOutlined,
  WarningOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { io, Socket } from "socket.io-client";
import {
  CONVERSATIONS_API_URL,
  isConversationsConfigured,
  Channel,
  ChannelConnectionStatus,
  TemplatePublication,
  conversationsApi,
  getAccessToken,
} from "@/services/conversations.api";
import { ChannelPermissions, ChannelPermissionsRef } from "./ChannelPermissions";
import { PhoneNumberInput } from "@/components/phone-number-input";
// Una restricción sin fecha de vencimiento también cuenta: ver services/channel-restriction.
import { estaRestringido, textoVencimiento } from "@/services/channel-restriction";
import { isCloudApi } from "@/services/channel-types";
import { CloudChannelCard } from "./CloudChannelCard";
import { CloudTemplatesSection } from "./CloudTemplatesSection";
import { ChannelAgentCard } from "./ChannelAgentCard";
import { estadoCloud } from "./cloud-channel";

const { Title, Text, Paragraph, Link } = Typography;


/** Campo "Status" del form (Bug 22): punto de color + label del estado de conexión. */
const STATUS_FIELD: Record<ChannelConnectionStatus, { color: string; label: string }> = {
  connected: { color: "#52c41a", label: "Conectado" },
  qr_pending: { color: "#1677ff", label: "Esperando escaneo" },
  disconnected: { color: "#8c8c8c", label: "Desconectado" },
  // Rojo y no gris: no es "está caído", es "WhatsApp castigó la cuenta". La diferencia importa
  // porque desconectado se arregla reconectando y esto no — hay que esperar.
  restricted: { color: "#ff4d4f", label: "Restringido" },
};

/**
 * El estado de un canal de la Cloud API se dice distinto: no hay QR que esperar, y un desconectado puede
 * ser uno que nunca se conectó o uno que Meta desconectó, que se arreglan de formas distintas.
 */
function statusField(channel: Channel | undefined, status: ChannelConnectionStatus) {
  if (!channel || !isCloudApi(channel.type)) return STATUS_FIELD[status];
  if (status !== "disconnected") return STATUS_FIELD[status];
  return estadoCloud({ config: channel.config, connectionStatus: status }).conectadoAlgunaVez
    ? { color: "#ff4d4f", label: "Desconectado por Meta" }
    : { color: "#8c8c8c", label: "Sin conectar" };
}

/** ¿el identifier es el placeholder (channelId) que pusimos al crear sin número? */
const isPlaceholder = (c: Channel) => c.identifier === c.channelId;

export const ChannelDetail = () => {
  // Ruta: /stores/channels/:id(storeCode)/:channelId. `id` local = el canal (compat con el resto).
  const { token } = theme.useToken();
  const { id: storeCode = "", channelId: id = "" } = useParams();
  const [searchParams] = useSearchParams();
  // ?new=1 (viene del alta) → modo creación: form editable, footer solo Save (bugs 10/20).
  const isNew = searchParams.get("new") === "1";
  const { push } = useNavigation();
  const backToList = () => push(`/stores/channels/${storeCode}`);

  async function handleCancelNew() {
    try { await conversationsApi.deleteChannel(id); } catch { /* silent */ }
    backToList();
  }

  const nameFieldRef = useRef<HTMLDivElement>(null);
  const numberFieldRef = useRef<HTMLDivElement>(null);

  const [channel, setChannel] = useState<Channel>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const editing = true; // siempre en modo edición al abrir un canal existente
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [active, setActive] = useState(true);
  const [nameError, setNameError] = useState(false);
  const [numberError, setNumberError] = useState(false);
  const [status, setStatus] = useState<ChannelConnectionStatus>("disconnected");
  const [qrDataUrl, setQrDataUrl] = useState<string>();
  /** #177: vencimiento de la restricción de cuenta (ISO), o `null` si no hay. */
  const [restrictedUntil, setRestrictedUntil] = useState<string | null>(null);
  // El tipo se sigue aparte del vencimiento porque una restricción puede no traer fecha (la que
  // deducimos de una racha de timeouts, por ejemplo). Sin esto, esa restricción no se vería hasta
  // recargar: el estado en vivo sólo tenía la fecha, que llega en `null`.
  const [restrictionType, setRestrictionType] = useState<string | null>(null);
  /** La última copia de una plantilla que cambió (`channel:templates`): la sección la refleja. */
  const [templateEvent, setTemplateEvent] = useState<TemplatePublication | null>(null);
  const socketRef = useRef<Socket | null>(null);
  /** El tipo, para los handlers del socket, que se registran antes de que cargue el canal. */
  const channelTypeRef = useRef<string | undefined>(undefined);
  const permissionsRef = useRef<ChannelPermissionsRef>(null);
  const restricted = estaRestringido({ restrictedUntil, restrictionType });
  const cloud = isCloudApi(channel?.type);

  // Campos editables en creación (isNew) o al presionar "Edit"; en vista, solo lectura.
  const editable = isNew || editing;

  // ── socket de la página (QR + estado) ──────────────────────────────────────
  useEffect(() => {
    // Sin URL del conv-service no hay a dónde conectarse: `""` haría que socket.io y axios
    // peguen contra el dominio del admin, que devuelve el HTML del index.
    if (!isConversationsConfigured()) return;

    const socket = io(`${CONVERSATIONS_API_URL}/conversations`, {
      transports: ["websocket"],
      auth: { token: getAccessToken() },
    });
    socketRef.current = socket;
    // Un socket que se reconecta vuelve sin rooms: sin esto, la ficha deja de recibir el estado.
    socket.on("connect", () => socket.emit("channel:join", { channelId: id }));
    // Templates (Fase 2): el estado y la categoría que Meta le da a cada copia, en vivo.
    socket.on("channel:templates", (p: { channelId: string; publication: TemplatePublication }) => {
      if (p.channelId !== id) return;
      setTemplateEvent(p.publication);
    });
    socket.on("channel:qr", (p: { channelId: string; qrDataUrl: string }) => {
      if (p.channelId !== id) return;
      setStatus("qr_pending");
      setQrDataUrl(p.qrDataUrl);
    });
    socket.on(
      "channel:status",
      (p: {
        channelId: string;
        status: ChannelConnectionStatus;
        loggedOut?: boolean;
        restrictedUntil?: string | null;
      }) => {
        if (p.channelId !== id) return;
        setStatus(p.status);
        // Cloud API: el motivo de una desconexión y los datos del número viven en `config`. El aviso de
        // conectado lo da la card, que es la que conectó.
        const esCloud = isCloudApi(channelTypeRef.current);
        if (esCloud) void refresh();
        if (p.status === "connected") {
          setQrDataUrl(undefined);
          if (!esCloud) message.success("WhatsApp conectado");
        }
        // #177: `restrictedUntil` presente ⇒ el corte fue por restricción de cuenta. Mandar a
        // re-escanear ahí es un consejo equivocado: la sesión no es el problema.
        if (p.restrictedUntil) setRestrictedUntil(p.restrictedUntil);
        else if (p.loggedOut) message.warning("La sesión se cerró, re-escaneá el QR");
      },
    );
    // #177: la restricción se pone y se levanta sola; el panel tiene que seguirla sin recargar.
    socket.on(
      "channel:restriction",
      (p: { channelId: string; active: boolean; until: string | null; type: string | null }) => {
        if (p.channelId !== id) return;
        setRestrictedUntil(p.active ? p.until : null);
        setRestrictionType(p.active ? p.type : null);
        if (!p.active) message.success("WhatsApp levantó la restricción de este número");
      },
    );
    // Cloud API (D16): el avance del sync inicial del celular, que viaja entero en el evento.
    socket.on("channel:sync", (p: { channelId: string; sync: Record<string, unknown> }) => {
      if (p.channelId !== id) return;
      setChannel((c) => (c ? { ...c, config: { ...(c.config ?? {}), sync: p.sync } } : c));
    });
    // Cloud API (D2): Meta rechazó un mensaje por el método de pago de la cuenta.
    socket.on("channel:payment", (p: { channelId: string }) => {
      if (p.channelId === id) void refresh();
    });
    return () => {
      socket.emit("channel:leave", { channelId: id });
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ── carga del canal + arranque del QR ──────────────────────────────────────
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function load() {
    setLoading(true);
    try {
      const c = await conversationsApi.getChannel(id);
      setChannel(c);
      channelTypeRef.current = c.type;
      // Bug 15: al crear, el toggle Active arranca en OFF (no importa el default del backend).
      setActive(isNew ? true : c.isActive);
      setStatus(c.connectionStatus);
      setName(c.name === "Nuevo canal" ? "" : c.name);
      setNumber(isPlaceholder(c) ? "" : c.identifier);
      socketRef.current?.emit("channel:join", { channelId: id });
      setRestrictedUntil(c.restrictedUntil ?? null);
      setRestrictionType(c.restrictionType ?? null);
      // WhatsApp sin conectar → pedimos QR automáticamente.
      //
      // #177: salvo que WhatsApp tenga la CUENTA restringida. Ahí el canal figura desconectado
      // porque nos bajaron el socket, pero la sesión guardada sigue sirviendo: abrir el QR solo
      // invita a re-vincular, que no levanta la restricción y quema un dispositivo vinculado por
      // intento (el 2026-08-24 se quemaron tres en trece minutos).
      if (c.type === "whatsapp" && c.connectionStatus !== "connected" && !estaRestringido(c)) {
        void startQr();
      }
    } catch (e: any) {
      message.error(`No se pudo cargar el canal: ${e?.response?.data?.message ?? e?.message ?? e}`);
    } finally {
      setLoading(false);
    }
  }

  /**
   * El canal de nuevo, sin tocar el form: lo que cambió lo escribió el backend (un evento de Meta, la
   * salud del número), y pisar lo que el admin está editando sería perderlo.
   */
  async function refresh() {
    try {
      const c = await conversationsApi.getChannel(id);
      setChannel(c);
      setStatus(c.connectionStatus);
      setRestrictedUntil(c.restrictedUntil ?? null);
      setRestrictionType(c.restrictionType ?? null);
    } catch {
      /* el próximo evento o una recarga lo trae */
    }
  }

  /**
   * Lo que devuelve el backend al conectar o confirmar el pago: el canal ya actualizado. Después se pide
   * de nuevo, porque la respuesta del signup se arma antes de arrancar el sync (D23): los `channel:sync`
   * que llegaron por el socket mientras tanto quedarían pisados por un `config` sin `sync`.
   */
  function applyChannel(c: Channel) {
    setChannel(c);
    setStatus(c.connectionStatus);
    setNumber(isPlaceholder(c) ? "" : c.identifier);
    setRestrictedUntil(c.restrictedUntil ?? null);
    setRestrictionType(c.restrictionType ?? null);
    void refresh();
  }

  async function startQr() {
    try {
      await conversationsApi.startQr(id);
      setStatus("qr_pending");
    } catch (e: any) {
      message.error(`No se pudo generar el QR: ${e?.response?.data?.message ?? e?.message ?? e}`);
    }
  }

  async function handleDelete() {
    try {
      await conversationsApi.deleteChannel(id);
      message.success("Canal eliminado");
      backToList();
    } catch (e: any) {
      message.error(`No se pudo eliminar: ${e?.response?.data?.message ?? e?.message ?? e}`);
    }
  }

  async function handleSave() {
    const nameEmpty = !name.trim();
    // Cloud API: el número lo pone Meta al conectar, no el admin.
    const numberEmpty = !cloud && !number.trim();
    setNameError(nameEmpty);
    setNumberError(numberEmpty);
    if (nameEmpty || numberEmpty) {
      message.error("Completá los campos requeridos");
      const firstError = nameEmpty ? nameFieldRef : numberFieldRef;
      firstError.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSaving(true);
    try {
      const num = number.trim();
      await conversationsApi.updateChannel(id, {
        name: name.trim() || "Nuevo canal",
        // Sin número → mantenemos el placeholder (channelId) para no romper el unique. En la Cloud API no
        // se manda: el backend lo rechaza, porque pisaría el número que puso Meta.
        ...(cloud ? {} : { identifier: num || channel?.channelId }),
        isActive: active,
      });
      await permissionsRef.current?.persist();
      message.success("Canal guardado");
      // Creación → volvemos al listado; edición → volvemos a modo vista y recargamos.
      if (isNew) {
        backToList();
      } else {
        await load();
      }
    } catch (e: any) {
      message.error(`No se pudo guardar: ${e?.response?.data?.message ?? e?.message ?? e}`);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  const title =
    !isNew && channel && channel.name && channel.name !== "Nuevo canal"
      ? channel.name
      : cloud
        ? "Nuevo canal WhatsApp Business"
        : "Nuevo canal WhatsApp QR";
  const currentStatus = statusField(channel, status);

  return (
    <div>
      {/* Header — sin badge de estado (Bug 14/18); el estado va en el campo "Status". */}
      <Flex align="center" style={{ marginBottom: 20 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={isNew ? handleCancelNew : backToList} style={{ width: 32, height: 32, padding: 0, marginRight: 16 }} />
        <Title level={3} style={{ margin: 0 }}>
          {title}
        </Title>
      </Flex>

      {/* Name / Number / Activo */}
      <div ref={nameFieldRef} style={{ marginBottom: 16 }}>
        <Text style={{ display: "block", marginBottom: 4 }}>
          Nombre <span style={{ color: "red" }}>*</span>
        </Text>
        {editable ? (
          <Input
            value={name}
            onChange={(e) => { setName(e.target.value); setNameError(false); }}
            placeholder="Nombre del canal"
            style={{ maxWidth: 360 }}
            status={nameError ? "error" : ""}
          />
        ) : (
          <Text>{name || "—"}</Text>
        )}
      </div>
      <div ref={numberFieldRef} style={{ marginBottom: 16 }}>
        <Text style={{ display: "block", marginBottom: 4 }}>
          Número {!cloud && <span style={{ color: "red" }}>*</span>}
        </Text>
        {cloud ? (
          // Lo pone Meta al conectar (D29): editarlo acá lo desalinearía del número real.
          number ? <Text>{number}</Text> : <Text type="secondary">Se completa al conectar el número con Facebook</Text>
        ) : editable ? (
          <PhoneNumberInput
            value={number}
            onChange={(val) => { setNumber(val); setNumberError(false); }}
            placeholder="Ej: +54 351 ..."
            containerStyle={{
              maxWidth: 360,
              ...(numberError && {
                borderRadius: 5,
                boxShadow: "0 0 0 1px #ff4d4f, 0 0 0 3px rgba(255,38,5,0.06)",
              }),
            }}
          />
        ) : (
          <Text>{number || "—"}</Text>
        )}
      </div>
      <div style={{ marginBottom: 16 }}>
        <Flex align="center" gap={8}>
          <Text>Activo</Text>
          <Switch checked={active} onChange={setActive} disabled={!editable} />
        </Flex>
      </div>

      {/* Status (Bug 22) — punto de color + estado de conexión. No se muestra en creación. */}
      {!isNew && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
            Estado
          </Text>
          <Space size={6} align="center">
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: currentStatus.color,
                display: "inline-block",
              }}
            />
            <Text>{currentStatus.label}</Text>
          </Space>
        </div>
      )}

      {/* ── Explicación WhatsApp QR ── */}
      {channel?.type === "whatsapp" && (
      <div style={{ marginBottom: 16 }}>
        <Paragraph style={{ marginBottom: 8 }}>
          Conecta tu número escaneando un QR, igual que WhatsApp Web. Esto te permite usar{" "}
          <Text strong>tu número actual</Text> sin trámites de aprobación, sin costo por
          conversación y con historial disponible desde el día uno.
        </Paragraph>
        <Paragraph style={{ marginBottom: 4 }}>
          Es importante que sepas que no es la{" "}
          <Text strong>API oficial de WhatsApp (Meta)</Text>. Eso implica:
        </Paragraph>
        <ul style={{ paddingLeft: 20, marginBottom: 8 }}>
          <li>
            <Text strong>La sesión puede caerse</Text> y requerir que vuelvas a escanear el
            QR. Mientras esté desconectada no se envían ni reciben mensajes.
          </li>
          <li>
            <Text strong>WhatsApp puede restringir o bloquear el número</Text> si detecta
            uso masivo o reportes de spam. El riesgo lo asume la cuenta conectada.
          </li>
          <li>
            <Text strong>Actualizaciones de WhatsApp pueden interrumpir el servicio</Text>{" "}
            temporalmente hasta que adaptemos la integración.
          </li>
        </ul>
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>Recomendaciones:</Text> usá un número dedicado al negocio (no el
          personal), escribí solo a contactos que te contactaron primero y sobre todo evitá
          envíos masivos.
        </Paragraph>
      </div>
      )}

      {/*
        #177: cartel de restricción de cuenta. Va arriba de todo lo de vinculación porque contradice
        lo que esa sección invita a hacer: acá el QR no es la solución.
      */}
      {restricted && cloud && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Meta restringió este número"
          description={
            <>
              No se pueden enviar mensajes desde el panel <Text strong>{textoVencimiento(restrictedUntil)}</Text>
              {restrictionType ? <> ({restrictionType})</> : null}. Lo que el contacto escriba sigue entrando. El
              detalle está en WhatsApp Manager, en la calidad de la cuenta.
            </>
          }
        />
      )}

      {restricted && !cloud && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="WhatsApp restringió este número"
          description={
            <>
              No se pueden enviar mensajes{" "}
              <Text strong>{textoVencimiento(restrictedUntil)}</Text>
              {restrictionType ? <> ({restrictionType})</> : null}. La restricción es
              de la <Text strong>cuenta</Text>, no de la sesión: <Text strong>volver a escanear el QR
              no la levanta</Text> y cada vinculación consume uno de los dispositivos que WhatsApp
              permite. Se libera sola al vencer.
              <br />
              Mientras dure, <Text strong>se puede responder desde el teléfono y desde WhatsApp
              Web</Text>: esos mensajes igual entran al panel. Suele dispararse por escribirle a
              contactos que no escribieron primero, abrir muchas conversaciones nuevas en poco
              tiempo, o acumular reportes.
            </>
          }
        />
      )}

      {/* ── Conectar con Facebook (Cloud API) ── */}
      {/* También al crearlo: el alta sigue con conectar el número, como la de QR sigue con escanear. */}
      {cloud && channel && <CloudChannelCard channel={channel} status={status} onChannel={applyChannel} />}
      {/* ── Plantillas de WhatsApp (templates) ── */}
      {cloud && channel && !isNew && <CloudTemplatesSection channel={channel} status={status} evento={templateEvent} />}

      {/* ── Log into WhatsApp Web ── */}
      {channel?.type === "whatsapp" && (
      <Card title={<span style={{ fontSize: 14 }}>Iniciá sesión en WhatsApp Web</span>} style={{ marginBottom: 16, borderColor: token.colorBorder }} styles={{ header: { minHeight: 38, padding: "0 16px" } }}>
        <Flex gap={24} wrap="wrap" justify="space-between">
          <div style={{ flex: 1, minWidth: 280 }}>
            <Paragraph type="secondary">
              Escaneá este código QR para vincular una cuenta de WhatsApp existente a este dispositivo.
            </Paragraph>
            <ol style={{ paddingLeft: 18, color: "rgba(0,0,0,0.65)", lineHeight: 2 }}>
              <li>Abrí WhatsApp en tu celular</li>
              <li>Tocá Configuración en iPhone o Menú en Android</li>
              <li>Tocá Dispositivos vinculados y luego Vincular dispositivo</li>
              <li>Apuntá tu celular a esta pantalla para escanear el QR</li>
              <li>Mantené ambas apps abiertas y con conexión a internet</li>
            </ol>
            {/* Bug 11: dos enlaces (ayuda + login por número). */}
            <Link href="#">¿Necesitás ayuda para empezar?</Link>
            <br />
            <Link href="#">Iniciar sesión con número de teléfono</Link>
          </div>

          <div style={{ flex: "0 0 33%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            {restricted ? (
              // #177: donde iría el QR va la explicación. Ofrecer un QR acá es activamente dañino:
              // no levanta la restricción y consume un slot de dispositivo vinculado.
              <Flex
                vertical
                align="center"
                justify="center"
                gap={10}
                style={{ width: 220, minHeight: 220, margin: "0 auto", padding: 12, textAlign: "center" }}
              >
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    background: "#FFF1F0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <WarningOutlined style={{ fontSize: 36, color: "#FF4D4F" }} />
                </div>
                <div style={{ fontSize: 16, color: "#262626" }}>Cuenta restringida</div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {textoVencimiento(restrictedUntil)}
                </Text>
              </Flex>
            ) : status === "connected" ? (
              // Bug 19: estado conectado = check verde + "Connected", sin botón Desconectar.
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    background: "#52c41a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckOutlined style={{ fontSize: 36, color: "#fff" }} />
                </div>
                <div style={{ marginTop: 8, fontSize: 16, color: "#262626" }}>Conectado</div>
              </div>
            ) : qrDataUrl ? (
              // Bug 12: QR con el logo de WhatsApp superpuesto en el centro.
              <div style={{ position: "relative", width: 220, height: 220, margin: "0 auto" }}>
                <img src={qrDataUrl} alt="QR de WhatsApp" style={{ width: 220, height: 220 }} />
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    background: "#fff",
                    borderRadius: "50%",
                    padding: 6,
                    display: "flex",
                    boxShadow: "0 0 0 3px #fff",
                  }}
                >
                  <WhatsAppOutlined style={{ fontSize: 34, color: "#25D366" }} />
                </div>
              </div>
            ) : (
              <Flex
                align="center"
                justify="center"
                style={{ width: 220, height: 220, margin: "0 auto", border: "1px dashed #d9d9d9", borderRadius: 8 }}
              >
                <Spin tip="Generando QR…" />
              </Flex>
            )}
          </div>
        </Flex>
      </Card>
      )}

      {/* ── Asistente IA del canal (xcons-docs#566) ── */}
      {channel && !isNew && <ChannelAgentCard channel={channel} />}

      {/* Permisos — sección separada (operadores + sellers). Solo lectura en modo vista. */}
      {channel && (
        <ChannelPermissions
          ref={permissionsRef}
          channelId={id}
          storeCode={channel.storeCode}
          editable={editable}
          isNew={isNew}
        />
      )}

      {/* Footer por modo:
          - creación:  Volver + Guardar
          - existente: Eliminar canal + Guardar */}
      <Flex justify="space-between" style={{ marginTop: 20 }}>
        {isNew ? (
          <Button icon={<ArrowLeftOutlined />} onClick={handleCancelNew}>
            Volver
          </Button>
        ) : (
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() =>
              Modal.confirm({
                title: "¿Quieres eliminar este canal?",
                okText: "Eliminar",
                okType: "danger",
                cancelText: "Cancelar",
                onOk: handleDelete,
              })
            }
          >
            Eliminar canal
          </Button>
        )}

        <Button
          type="primary"
          icon={<CheckOutlined />}
          iconPosition="end"
          loading={saving}
          onClick={handleSave}
        >
          Guardar
        </Button>
      </Flex>
    </div>
  );
};

export default ChannelDetail;
